import type { RegisteredTenantCreateOperation } from '@festina/configuration-general-types/configuration'
import { notEmpty, typeUtils } from '@festina-life/core'
import { FormikForm, FormikTextInput } from '@festina-life/ui/formikForms'
import { useTranslate } from '@festina-life/ui/hooks'
import { useMemo } from 'react'
import * as Yup from 'yup'

import { useMetaModelContext } from '../../context/metaModelContext'
import { getApplicationViewRegisteredTenants } from '../../workspace/helpers'
import { useOperationsContext } from '../../workspace/operationsContext'
import { DraftCreationFormId } from '../draftCreationModal/DraftCreationModal'
import { draftCreationStatusReporter } from '../draftCreationModal/draftCreationStatusReporter'
import type {
  DraftCreatedResult,
  DraftCreationProps,
} from '../draftCreationModal/types'

type TenantDraftCreationData = {
  tenantId: string
  tenantName: string
}

const validateKey = typeUtils.createValidator<TenantDraftCreationData>()

export const TenantDraftCreationForm = ({
  layer,
  block,
  onStatusChanged,
}: DraftCreationProps<'tenant'>) => {
  const { onAddOrUpdateOperation, registeredTenantCreateOperations } =
    useOperationsContext()
  const translate = useTranslate()
  const { blockTopology } = useMetaModelContext()

  const initialValues: TenantDraftCreationData = {
    tenantId: '',
    tenantName: '',
  }
  const registeredTenants = useMemo(
    () =>
      getApplicationViewRegisteredTenants({
        block,
        blockTopology,
        registeredTenantCreateOperations,
      })
        .map(({ internalOperation }) => internalOperation.registeredTenant)
        .filter(notEmpty),
    [registeredTenantCreateOperations, block, blockTopology],
  )

  const validationSchema: Yup.ObjectSchema<TenantDraftCreationData> =
    Yup.object({
      tenantId: Yup.string()
        .required(translate('validation.required'))
        .test(
          'noDuplicates',
          translate('validation.unique'),
          (value) =>
            !registeredTenants?.some(({ tenantId }) => tenantId === value),
        ),
      tenantName: Yup.string()
        .required(translate('validation.required'))
        .test(
          'noDuplicates',
          translate('validation.unique'),
          (value) =>
            !registeredTenants?.some(({ tenantName }) => tenantName === value),
        ),
    })

  const handleSubmit = async (
    values: TenantDraftCreationData,
  ): Promise<DraftCreatedResult> => {
    const createOperation = {
      _type: 'RegisteredTenantCreateOperation',
      registeredTenant: {
        _type: 'RegisteredTenant',
        tenantName: values.tenantName,
        tenantId: values.tenantId,
      },
    } satisfies RegisteredTenantCreateOperation

    const createdOnWorkspaceKey = await onAddOrUpdateOperation({
      operation: createOperation,
      layer,
      block,
      shouldOpenUnifiedBranchWorkspace: true,
    })

    return {
      createdDraft: {
        type: 'tenant',
        name: values.tenantId,
        layer,
        block,
      },
      addedToWorkspaceKey: createdOnWorkspaceKey,
    }
  }

  return (
    <FormikForm
      formId={DraftCreationFormId}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={draftCreationStatusReporter(onStatusChanged, handleSubmit)}
      shouldSanitizeOnSubmit={false}
    >
      <FormikTextInput
        name={validateKey('tenantId')}
        label={translate('tenantDraftForm.tenantId.label')}
      />
      <FormikTextInput
        name={validateKey('tenantName')}
        label={translate('tenantDraftForm.tenantName.label')}
      />
    </FormikForm>
  )
}
